import { usePagination } from '@mui/lab/Pagination';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import { Suspense, type SyntheticEvent, lazy, useMemo } from 'react';
import { type UseFormSetValue } from 'react-hook-form';
import { type NavigateFunction } from 'react-router';
import { type Car, type Filter } from '../../_types/Car';
import { useRootContainerContext } from '../../context/root-container';
import { useSettingsContext } from '../../context/settings';
import { useResponsive } from '../../hooks/use-responsive';
import useLocales from '../../hooks/useLocales';
import { Modules } from '../../modules/_definition/types';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import { CalcFdType } from '../calculator/types';
import { getLowestRate } from '../car-price-grid/helpers';
import type { LowestRate } from '../car-price-grid/types';
import CarTileGridSkeleton from '../car-tile/car-tile-grid-skeleton';
import CarTileListMiniSkeleton from '../car-tile/car-tile-list-mini-skeleton';
import CarTileListSkeleton from '../car-tile/car-tile-list-skeleton';

const CarTileGrid = lazy(() => import('../car-tile/car-tile-grid' /* webpackChunkName: "CarTileGrid" */));
const CarTileList = lazy(() => import('../car-tile/car-tile-list' /* webpackChunkName: "CarTileList" */));
const CarTileListMini = lazy(() => import('../car-tile/car-tile-list-mini' /* webpackChunkName: "CarTileListMini" */));

type CarListViewProps = {
    cars: Record<string, Car<false>>;
    disablePaging?: boolean;
    filter: Filter;
    list?: boolean;
    loading: boolean;
    mini?: boolean;
    module: Modules;
    numFound: number;
    onFilters: UseFormSetValue<Filter>;
    onMiniSelectCar?: ((e: SyntheticEvent<any>, car: Car<false>) => void) | undefined;
} & (
        | {
            mini?: true;
            onMiniSelectCar?: (e: SyntheticEvent<any>, car: Car<false>) => void;
        }
        | {
            mini?: false;
            navigate: NavigateFunction;
            onMiniSelectCar?: undefined;
        }
    );

export default function CarListView(props: CarListViewProps) {
    const { cars, disablePaging, filter, list, loading, mini, module, numFound, onFilters, onMiniSelectCar } = props;
    const settings = useSettingsContext();
    const rootContainer = useRootContainerContext();
    const { translate } = useLocales();
    const xs = useResponsive(rootContainer, 'down', 'sm');
    const sm = useResponsive(rootContainer, 'down', 'md');
    const CarTileElement = useMemo(() => (mini ? CarTileListMini : list ? CarTileList : CarTileGrid), [list, mini]);
    const CarTileSkeletonElement = useMemo(() => (mini ? CarTileListMiniSkeleton : list ? CarTileListSkeleton : CarTileGridSkeleton), [list, mini]);

    const pages = useMemo(() => Math.ceil(numFound / settings?.[module]?.forcedFilter?.pageSize), [module, numFound, settings]);
    const { items } = usePagination({
        boundaryCount: xs || sm || pages < 3 ? 0 : 1,
        count: !checkUndefinedNullOrEmpty(pages) && pages > 0 ? pages : 1,
        page: filter?.moreCars || 1,
        disabled: loading || settings?.general?.mockup,
        siblingCount: xs || sm || pages < 3 ? 0 : 1,
    });

    const carsToUse: Record<string, Car<false>> = useMemo(() => {
        let returnObject = {};
        if (loading === true) {
            for (let i = 0; i < filter?.pageSize; i++) {
                returnObject[i?.toString()] = { id: i };
            }
        } else {
            returnObject = cars || {};
        }

        return returnObject;
    }, [cars, filter?.pageSize, loading]);

    return (
        <>
            <Grid container spacing={2} sx={{ maxHeight: '100%', overflow: 'visible' }}>
                {Object?.keys(carsToUse)?.map((idCar, index) => {
                    const car = carsToUse?.[idCar];
                    return (
                        <Grid
                            key={`CarListView_CarTile_${car.id}`}
                            size={{
                                xs: 12,
                                sm: settings?.[module]?.limitCols <= 1 || list || mini ? undefined : 6,
                                md: settings?.[module]?.limitCols <= 2 || list || mini ? undefined : 4,
                                xl: settings?.[module]?.limitCols <= 3 || list || mini ? undefined : 3,
                            }}
                        >
                            <Suspense fallback={<CarTileSkeletonElement rootContainer={rootContainer} settings={settings} />}>
                                <CarTileElement
                                    car={car}
                                    loading={loading}
                                    module={module}
                                    navigate={mini === false ? props?.navigate : undefined}
                                    onMiniClick={onMiniSelectCar}
                                    showMonthlyRate={
                                        module === Modules?.Finanzierungsansicht
                                            ? !checkUndefinedNullOrEmpty(car?.finanzierung)
                                                ? { type: CalcFdType.Financing, rate: car?.finanzierung?.monatliche_rate }
                                                : undefined
                                            : !checkUndefinedNullOrEmpty(filter?.fd) || !checkUndefinedNullOrEmpty(filter?.Rate)
                                                ? getLowestRate(
                                                    car,
                                                    !checkUndefinedNullOrEmpty(filter?.fd)
                                                        ? [
                                                            !filter?.fd?.includes(1) ? CalcFdType.Financing : undefined,
                                                            ...(!filter?.fd?.includes(2) ? [CalcFdType.Leasing, 'leasingComm' as LowestRate['type']] : []),
                                                        ]?.filter((e) => !checkUndefinedNullOrEmpty(e))
                                                        : undefined
                                                )
                                                : undefined
                                    }
                                    sourceList={0}
                                    sourceIndex={index}
                                />
                            </Suspense>
                        </Grid>
                    );
                })}
            </Grid>
            {!disablePaging && (
                <Stack gap={2} data-testid="Pager_Container" direction="row" justifyContent="center" flexWrap="nowrap" sx={{ mt: 2 }}>
                    {items
                        ?.filter((e) => !xs || (e?.type === 'page' && e?.selected === true) || e?.type === 'previous' || e?.type === 'next')
                        ?.map((e) => (
                            <Button
                                data-testid="Pager_Pagebutton"
                                key={`CarListView_Pager_${e?.type}_${e?.page}`}
                                color="primary"
                                disabled={e?.disabled || settings?.general?.mockup}
                                variant={e?.selected ? 'contained' : 'outlined'}
                                onClick={function () {
                                    if (e?.type !== 'start-ellipsis' && e?.type !== 'end-ellipsis' && e?.selected !== true) {
                                        onFilters('moreCars', e?.page);
                                    }
                                }}
                                sx={{
                                    [getStyleQuery()]: { 'outline': 'none', 'transitionDuration': '250ms, 0ms, 250ms, 250ms', '&:hover': { outline: 'none !important', boxShadow: 'none !important' } },
                                }} // transition-duration 0ms for box-shadow problem Safari
                            >
                                {e?.type === 'previous'
                                    ? translate('common:buttons.prevPage')
                                    : e?.type === 'next'
                                        ? translate('common:buttons.nextPage')
                                        : e?.type === 'start-ellipsis' || e?.type === 'end-ellipsis'
                                            ? '...'
                                            : e?.page}
                            </Button>
                        ))}
                </Stack>
            )}
        </>
    );
}
